import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { productsApi, api } from '@/lib/api-client';

export function useLocations(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ['locations'],
    queryFn: () => productsApi.get<{ data: any[] }>('/locations'),
    enabled: options?.enabled ?? true,
  });
}

export function useCreateLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      name: string;
      nickname?: string;
      address?: string;
      /** Contact email vendors reach the location at (PO Reply-To). */
      email?: string;
      taxRate?: number;
      type?: 'company' | 'warehouse' | 'private_warehouse';
      /** IANA timezone id (e.g. America/Los_Angeles). Omitted → server default (Pacific). */
      timezone?: string;
    }) => productsApi.post<{ data: any }>('/locations', body),
    onSuccess: (response) => {
      // Immediately inject the new location into the cache so the LocationPicker
      // never sees the newly-set selectedLocationId as "invalid" and resets it
      // back to the first location before the background refetch completes.
      const newLocation = response?.data;
      if (newLocation) {
        queryClient.setQueryData(['locations'], (old: { data: any[] } | undefined) =>
          old ? { ...old, data: [...old.data, newLocation] } : old,
        );
      }
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
}

export function useUpdateLocation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      name?: string;
      nickname?: string;
      address?: string;
      /** Contact email vendors reach the location at (PO Reply-To); '' clears it. */
      email?: string | null;
      taxRate?: number;
      type?: 'company' | 'warehouse' | 'private_warehouse';
      /** IANA timezone id (e.g. America/Los_Angeles). */
      timezone?: string;
    }) => productsApi.put<{ data: any }>(`/locations/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['locations'] });
    },
  });
}

export function useUpdateDefaultTaxRate() {
  return useMutation({
    mutationFn: (defaultTaxRate: number) => api.put<{ data: any }>('/tenants/me/tax-rate', { defaultTaxRate }),
  });
}

// Fetches locations from the main API (includes externalId / MID)
export function useLocationsWithExternalId() {
  return useQuery({
    queryKey: ['locations-with-external-id'],
    queryFn: () => api.get<{ data: any[] }>('/locations'),
  });
}
